import React from 'react';
import { styled, Box } from '@staffino/primitive-controls';
import { View } from '@staffino/primitives';
import Avatar from './avatar';
import { Note, SubTitle, Title } from './typography';
import omitProps from './omit-props';
import { breakpoints } from './theme';

type Props = {
  image?: React.ReactNode | string | boolean;
  title?: JSX.Element | string;
  subtitle?: React.ReactNode | string | boolean;
  topComment?: React.ReactNode | string;
  rightCorner?: React.ReactNode | boolean;
  bottom?: React.ReactNode;
  body?: React.ReactNode;
  onPress?: (event) => void;
  collapse?: boolean;
} & Omit<React.ComponentProps<typeof Box>, 'title' | 'bottom'>;

const StyledTitle = styled(Title)(() => ({
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  webkitLineClamp: 2,
  webkitBoxOrient: 'vertical',
}));

const StyledSubtitle = styled(SubTitle)(() => ({
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'webkit-box',
  webkitLineClamp: 1,
  webkitBoxOrient: 'vertical',
}));

const TouchableOpacity = styled(View)((props) => ({
  userSelect: 'none',
  cursor: props.disabled ? 'default' : 'pointer',
}));

const Disappear = styled(omitProps(['collapse'], View))((props) => ({
  display: props.collapse ? 'none' : 'initial',
  [`@media (min-width: ${breakpoints[0]})`]: {
    display: 'initial',
  },
  height: '0 !important',
  overflow: 'hidden',
}));

const Media = ({
  image,
  title,
  subtitle,
  rightCorner = undefined,
  onPress,
  topComment,
  body,
  collapse = false,
  bottom,
  ...props
}: Props) => {
  let renderImage = image;
  let renderTopComment = topComment;
  let renderTitle: any = title;
  let renderSubtitle = subtitle;
  if (typeof image === 'string') {
    renderImage = <Avatar size={['small', 'base']} source={image} />;
  }
  if (typeof topComment === 'string') {
    renderTopComment = <Note>{topComment}</Note>;
  }
  if (typeof title === 'string') {
    renderTitle = <StyledTitle>{title}</StyledTitle>;
  }
  if (typeof subtitle === 'string') {
    renderSubtitle = <StyledSubtitle mt="2px">{subtitle}</StyledSubtitle>;
  }
  return (
    <Box flexDirection="column" {...props}>
      <TouchableOpacity disabled={!onPress} onPress={onPress}>
        <Box flexDirection="row">
          {renderImage}
          <Box flexDirection="column" pl={15} width="100%">
            {renderTopComment}
            {renderTitle}
            {renderSubtitle}
            {bottom}
          </Box>
          {rightCorner}
        </Box>
      </TouchableOpacity>
      {body && (
        <Box flexDirection="row" pl={collapse ? [0, 15] : 15}>
          <Disappear collapse={collapse}>{renderImage}</Disappear>
          {body}
        </Box>
      )}
    </Box>
  );
};

export default Media;
